<template>
  <div class="crm-mapping-manager">
    <div class="tw-max-w-4xl tw-mx-auto tw-p-6">
      <!-- Header -->
      <div class="tw-mb-8">
        <h1 class="tw-text-3xl tw-font-bold tw-text-gray-900 tw-mb-2">
          CRM Field Mapping
        </h1>
        <p class="tw-text-gray-600">
          Generate and edit AI-powered field mappings between your dossier template and CRM system.
        </p>
      </div>

      <!-- CRM Selection & Connection Status -->
      <div class="tw-bg-white tw-rounded-lg tw-shadow tw-p-6 tw-mb-6">
        <h2 class="tw-text-xl tw-font-semibold tw-mb-4">CRM Connection</h2>
        
        <div v-if="connectedCrms.length === 0" class="tw-text-center tw-py-8">
          <div class="tw-text-gray-400 tw-mb-4">
            <i class="fas fa-unlink tw-text-4xl"></i>
          </div>
          <p class="tw-text-gray-600 tw-mb-4">No CRM systems connected</p>
          <a href="/ui/advisor/integrations/" class="btn btn-primary">
            Connect CRM System
          </a>
        </div>

        <div v-else class="tw-space-y-4">
          <div>
            <label class="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
              Select CRM System
            </label>
            <select 
              v-model="selectedCrm" 
              class="tw-block tw-w-full tw-rounded-md tw-border-gray-300 tw-shadow-sm focus:tw-border-indigo-500 focus:tw-ring-indigo-500"
              @change="onCrmChange"
            >
              <option value="">Choose a CRM...</option>
              <option v-for="crm in connectedCrms" :key="crm" :value="crm">
                {{ crm }}
              </option>
            </select>
          </div>

          <div v-if="selectedCrm" class="tw-flex tw-items-center tw-space-x-2">
            <div class="tw-flex tw-items-center">
              <div class="tw-w-3 tw-h-3 tw-bg-green-400 tw-rounded-full tw-mr-2"></div>
              <span class="tw-text-sm tw-text-gray-600">Connected to {{ selectedCrm }}</span>
            </div>
            <div v-if="selectedCrm === 'Salesforce'" class="tw-text-xs tw-bg-blue-100 tw-text-blue-800 tw-px-2 tw-py-1 tw-rounded">
              Dynamic Schema Discovery
            </div>
          </div>
        </div>
      </div>

            <!-- Mapping Management -->
      <div v-if="selectedCrm" class="tw-space-y-6">
        
        <!-- Current Mapping Status -->
        <div class="tw-bg-white tw-rounded-lg tw-shadow tw-p-6">
          <div class="tw-flex tw-items-center tw-justify-between tw-mb-4">
            <h2 class="tw-text-xl tw-font-semibold">{{ selectedCrm }} Field Mapping</h2>
            
            <button
              @click="generateMapping"
              :disabled="isGenerating"
              class="btn btn-primary"
            >
              <i v-if="isGenerating" class="fas fa-spinner fa-spin tw-mr-1"></i>
              <i v-else class="fas fa-magic tw-mr-1"></i>
              {{ currentMapping ? 'Regenerate' : 'Generate' }} Mapping
            </button>
          </div>

          <!-- Template Information -->
          <div v-if="currentMapping && currentMapping._metadata" class="tw-mb-6 tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-lg tw-p-4">
            <h3 class="tw-text-sm tw-font-medium tw-text-blue-800 tw-mb-2">
              <i class="fas fa-template tw-mr-1"></i>
              Current Mapping Information
            </h3>
            <div class="tw-text-sm tw-text-blue-700">
              <p><strong>Template:</strong> {{ currentMapping._metadata.template_name }}</p>
              <p><strong>Last Updated:</strong> {{ new Date(currentMapping._metadata.last_updated).toLocaleDateString() }}</p>
              <p><strong>Total Mappings:</strong> {{ mappingSummary.totalMappings }} field mappings across {{ mappingSummary.objectTypes }} CRM objects</p>
            </div>
          </div>

          <!-- Generation Progress -->
          <div v-if="isGenerating" class="tw-text-center tw-py-8">
            <div class="tw-text-indigo-600 tw-mb-2">
              <i class="fas fa-brain fa-spin tw-text-3xl"></i>
            </div>
            <p class="tw-text-gray-600">
              AI is analyzing your {{ selectedCrm }} schema and generating field mappings...
            </p>
            <div class="tw-mt-4">
              <div class="tw-bg-gray-200 tw-rounded-full tw-h-2">
                <div class="tw-bg-indigo-600 tw-h-2 tw-rounded-full tw-transition-all tw-duration-300" :style="`width: ${generationProgress}%`"></div>
              </div>
            </div>
          </div>

          <!-- Mapping Editor -->
          <div v-if="currentMapping" class="tw-mt-6">
            <CrmMappingEditor
              :key="`${selectedCrm}-${currentMapping._metadata?.last_updated || Date.now()}`"
              :crm-type="selectedCrm.toLowerCase()"
              :initial-mapping="currentMapping"
              @mapping-saved="onMappingSaved"
            />
          </div>

          <!-- No Mapping State -->
          <div v-else-if="!isGenerating" class="tw-text-center tw-py-12 tw-border-2 tw-border-dashed tw-border-gray-300 tw-rounded-lg">
            <div class="tw-text-gray-400 tw-mb-4">
              <i class="fas fa-map tw-text-4xl"></i>
            </div>
            <h3 class="tw-text-lg tw-font-medium tw-text-gray-900 tw-mb-2">No field mapping generated yet</h3>
            <p class="tw-text-gray-500">
              Click "Generate Mapping" to create AI-powered field mappings using your firm's dossier template.
            </p>
          </div>
        </div>
      </div>


    </div>
  </div>
</template>

<script>
import { mapStores } from 'pinia';
import { useAdvisorStore } from '@frontend/ui/stores/advisorStore';
import { fetchWrapper } from '@frontend/shared/fetch-wrapper.js';
import CrmMappingEditor from './CrmMappingEditor.vue';
import { awaitTask } from '@frontend/ui/features/checkup/helpers/tasks';

export default {
  name: 'CrmMappingManager',
  
  components: {
    CrmMappingEditor,
  },
  
  data() {
    return {
      selectedCrm: '',
      connectedCrms: [],
      currentMapping: null,
      isGenerating: false,
      generationProgress: 0,
      firmMappings: {},
      includeSampleData: false,
    };
  },
  
  computed: {
    ...mapStores(useAdvisorStore),
    
    headers() {
      return {
        'X-CSRFToken': this.$root.$data.csrfToken,
        'Content-Type': 'application/json',
      };
    },
    
    mappingSummary() {
      if (!this.currentMapping || !this.currentMapping.object_mappings) {
        return { totalMappings: 0, objectTypes: 0, highConfidence: 0 };
      }
      
      const totalMappings = this.currentMapping.object_mappings.reduce(
        (total, obj) => total + obj.fields.length, 0
      );
      
      const objectTypes = this.currentMapping.object_mappings.length;
      
      const highConfidence = this.currentMapping.object_mappings.reduce(
        (total, obj) => total + obj.fields.filter(f => f.confidence === 'high').length, 0
      );
      
      return { totalMappings, objectTypes, highConfidence };
    },
  },
  
  async mounted() {
    await this.initialize();
  },
  
  methods: {
    async initialize() {
      try {
        // Get connected CRMs
        const res = await fetch('/api/v1/oauth/connections/', {
          method: 'GET',
          headers: this.headers,
        });
        const data = await res.json();
        
        this.connectedCrms = data.filter(app => 
          ['Salesforce'].includes(app)
        );
        
        // Load existing mappings
        await this.loadFirmMappings();
        
        if (this.connectedCrms.length > 0) {
          this.selectedCrm = this.connectedCrms[0];
          this.onCrmChange();
        }
      } catch (error) {
        console.error('Error initializing CRM mapping manager:', error);
      }
    },
    
    async loadFirmMappings() {
      try {
        const response = await fetchWrapper('/api/v1/crm/mappings/', {
          headers: this.headers,
        });
        
        if (response.ok) {
          const data = await response.json();
          this.firmMappings = data.mappings || {};
        }
      } catch (error) {
        console.error('Error loading firm mappings:', error);
      }
    },
    
    onCrmChange() {
      // Clear current mapping when CRM changes
      this.currentMapping = null;
      this.currentProposal = null;
      
      // Try to load existing mapping for this CRM type
      if (this.selectedCrm) {
        const mappingKey = this.selectedCrm.toLowerCase();
        if (this.firmMappings[mappingKey]) {
          this.currentMapping = this.firmMappings[mappingKey];
        }
      }
    },
    
    async generateMapping() {
      if (!this.selectedCrm) return;

      this.isGenerating = true;
      this.generationProgress = 0;

      const progressInterval = setInterval(() => {
        if (this.generationProgress < 90) {
          this.generationProgress += Math.random() * 10;
        }
      }, 500);

      try {
        const payload = {
          crm_type: this.selectedCrm.toLowerCase(),
          include_sample_data: this.includeSampleData,
        };

        const response = await awaitTask('/api/v1/crm/generate-mapping/', {
          method: 'POST',
          headers: this.headers,
          body: JSON.stringify(payload),
        });

        clearInterval(progressInterval);
        this.generationProgress = 100;

        const data = response.task.meta;
        
        // Reload firm mappings first to get the updated data
        await this.loadFirmMappings();
        
        // Then set the current mapping from the newly loaded firm mappings
        const mappingKey = this.selectedCrm.toLowerCase();
        if (this.firmMappings[mappingKey]) {
          this.currentMapping = this.firmMappings[mappingKey];
        } else {
          // Fallback to the response data if not found in firm mappings
          this.currentMapping = data.mapping;
        }
        
        // Show enhanced success message with firm-wide stats
        let message = `${this.selectedCrm} mapping generated successfully!`;

        if (data.mapping_stats) {
          const stats = data.mapping_stats;
          message += ` Generated ${stats.total_field_mappings} field mappings from template "${stats.template_analyzed}".`;
          
          // Indicate mapping approach
          if (this.includeSampleData) {
            message += ' (Sample data included - may be limited to existing fields)';
          } else {
            message += ' (Comprehensive schema-based mapping - all possible fields analyzed)';
          }
        }

        this.$oruga.notification.open({
          message: message,
          variant: 'success',
          duration: 8000,
        });
      } catch (error) {
        clearInterval(progressInterval);
        this.$oruga.notification.open({
          message: `Error generating CRM mapping: ${error.message}`,
          variant: 'danger',
        });
        console.error('Error generating mapping:', error);
      } finally {
        this.isGenerating = false;
        setTimeout(() => {
          this.generationProgress = 0;
        }, 1000);
      }
    },
    

    
    onMappingSaved(result) {
      // Refresh the current mapping from firm mappings
      this.loadFirmMappings().then(() => {
        // Reload the current mapping for this CRM type
        if (this.selectedCrm && this.firmMappings[this.selectedCrm.toLowerCase()]) {
          this.currentMapping = this.firmMappings[this.selectedCrm.toLowerCase()];
        }
      });
      
      this.$oruga.notification.open({
        message: `Mapping updated successfully! ${result.totalMappings} mappings saved for ${result.objectTypes.length} object types.`,
        variant: 'success',
        duration: 5000
      });
        },
  },
};
</script>

<style scoped>
.crm-mapping-manager {
  min-height: 100vh;
  background-color: #f9fafb;
}

.btn {
  @apply tw-inline-flex tw-items-center tw-px-4 tw-py-2 tw-border tw-text-sm tw-font-medium tw-rounded-md tw-shadow-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2;
}

.btn-primary {
  @apply tw-border-transparent tw-text-white tw-bg-indigo-600 hover:tw-bg-indigo-700 focus:tw-ring-indigo-500;
}

.btn-success {
  @apply tw-border-transparent tw-text-white tw-bg-green-600 hover:tw-bg-green-700 focus:tw-ring-green-500;
}

.btn-outline-secondary {
  @apply tw-border-gray-300 tw-text-gray-700 tw-bg-white hover:tw-bg-gray-50 focus:tw-ring-indigo-500;
}

.btn-outline-info {
  @apply tw-border-gray-300 tw-text-gray-700 tw-bg-white hover:tw-bg-gray-50 focus:tw-ring-indigo-500;
}

.btn-sm {
  @apply tw-px-3 tw-py-1.5 tw-text-xs;
}

.btn:disabled {
  @apply tw-opacity-50 tw-cursor-not-allowed;
}
</style> 
 